import { Component, OnDestroy, OnInit } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CommunicationService} from '../../../services/communication.service';
import {PermissionsService} from '../../../services/permissions.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MagicInsightsService} from '../../../services/magic-insights';
import {NotificationService} from '../../../services/notification.service';
import { Router } from '@angular/router';
import { Subject, timer } from 'rxjs';
import { exhaustMap, filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'manage-module',
  standalone: false,
  templateUrl: './manage-module.component.html',
  styleUrl: './manage-module.component.css'
})
export class ManageModuleComponent implements OnInit, OnDestroy {

  readonly MODEL_KEY = 'organisation_aira_module';
  readonly NOTIFICATION_TYPE = 'magic_insights_progress';

  dropDownSelectedModuleId: any;
  isSuperuser = false;
  moduleId: any;
  availableModules: any = null;
  moduleDetails: any;
  organisationUsers = [];
  organisationTeams = [];

  // Notification variables
  notifications: any[] = [];
  loadingNotifications = false;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private communicationService: CommunicationService,
    private permissionsService: PermissionsService,
    private snackbar: MatSnackBar,
    private magicInsightsService: MagicInsightsService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit() {
    this.moduleId = this.route.snapshot.queryParamMap.get('id');

    this.magicInsightsService.getMagicInsightsAvailableModules().subscribe(allMods => {
        this.availableModules = allMods;
        if (!this.moduleId){
            if (this.availableModules?.length){
              this.moduleId = this.availableModules[0].id;
            }
        }
        if (this.moduleId){
            this.dropDownSelectedModuleId = this.moduleId;

            this.loadModuleDetails(() => {
                this.communicationService.getIsSuperUserMessage().subscribe(isSuperuser => {
                this.isSuperuser = isSuperuser;
                  if (this.isSuperuser && this.moduleDetails?.perms_access) {
                      this.permissionsService
                        .getSuperuserTeamsAndUsers()
                        .subscribe(data => {
                          this.organisationTeams = data.organisation_teams;
                          this.organisationUsers = data.organisation_users;
                        });
                  }
                })
            });
        }
    });

    this.loadAndClearNotifications();
    this.startRunProgressRefresh();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onModuleChange() {
    if (this.dropDownSelectedModuleId !== this.moduleId){
      this.moduleId = this.dropDownSelectedModuleId;
      this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { id: this.moduleId },
          queryParamsHandling: 'merge', // Keep other existing params
        });
      this.loadModuleDetails();
    }
  }

  refreshModuleDetails() {
    this.loadModuleDetails();
  }

  getRunStatusLabel(status: string): string {
    return ({
      queued: 'Queued',
      processing: 'Processing',
      retrying: 'Retrying',
      delayed: 'Delayed',
      complete: 'Completed',
      completed_with_issues: 'Completed with issues',
      failed: 'Failed',
    })[status] || 'Preparing';
  }

  getRunStatusClass(status: string): string {
    return ({
      complete: 'bg-success text-white',
      completed_with_issues: 'bg-warning text-dark',
      failed: 'bg-danger text-white',
      retrying: 'bg-warning text-dark',
      delayed: 'bg-warning text-dark',
      processing: 'bg-info text-dark',
      queued: 'bg-secondary text-white',
    })[status] || 'bg-secondary text-white';
  }

  private loadModuleDetails(onLoaded?: () => void) {
    if (!this.moduleId) return;
    const requestedModuleId = this.moduleId;
    this.magicInsightsService.getMagicInsightsModuleInfo(requestedModuleId).subscribe(indMod => {
      if (this.moduleId === requestedModuleId && indMod && !Array.isArray(indMod)) {
        this.moduleDetails = indMod;
        onLoaded?.();
      }
    });
  }

  private startRunProgressRefresh() {
    timer(15_000, 15_000)
      .pipe(
        filter(() => document.visibilityState === 'visible' && this.hasActiveRuns()),
        exhaustMap(() => {
          const requestedModuleId = this.moduleId;
          return this.magicInsightsService.getMagicInsightsModuleInfo(requestedModuleId).pipe(
            map(moduleDetails => ({ requestedModuleId, moduleDetails }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ requestedModuleId, moduleDetails }) => {
        if (
          this.moduleId === requestedModuleId &&
          moduleDetails &&
          !Array.isArray(moduleDetails)
        ) {
          this.moduleDetails = moduleDetails;
        }
      });
  }

  private hasActiveRuns(): boolean {
    return Boolean(
      this.moduleDetails?.recent_runs?.some(run => run.progress && !run.progress.is_terminal)
    );
  }

  loadAndClearNotifications() {
    this.loadingNotifications = true;

    // 1. Fetch the notifications to display them
    this.notificationService.getNotifications(this.NOTIFICATION_TYPE).subscribe(
      (data) => {
        this.notifications = data;
        this.loadingNotifications = false;

        // 2. Once fetched, mark them as read so the badge clears
        this.markNotificationsRead();
      },
      (error) => {
        console.error('Error fetching notifications', error);
        this.loadingNotifications = false;
      }
    );
  }

  markNotificationsRead() {
    this.notificationService.markAllNotificationsAsRead(this.NOTIFICATION_TYPE).subscribe(() => {
        // 3. Manually update localStorage so the Navbar badge clears immediately
        localStorage.setItem('MagicInsightsAlertCount', '0');
        localStorage.setItem('lastMagicInsightsAlertCountCheck', new Date().getTime().toString());
    });
  }

  onPermissionChanged(event: any) {
    this.snackbar.open('Permissions updated')
  }

}
